import { Badge, RelativeTime, Tooltip, VisuallyHidden } from "@/components";
import { formatRelativeTime } from "@/lib/format";
import { useNow } from "@/lib/hooks";
import { countryName } from "@/lib/regions";
import type { Supplier, SupplierStatus, SyncRun } from "../api";
import styles from "./SupplierBits.module.css";

/** "every 9 hours", "every day", "every 3 days". */
export function checkCadence(hours: number): string {
  if (hours < 36) {
    const rounded = Math.max(1, Math.round(hours));
    return rounded === 1 ? "every hour" : `every ${rounded} hours`;
  }
  const days = Math.round(hours / 24);
  return days === 1 ? "every day" : `every ${days} days`;
}

/** When the next catalog check is due, as a sentence. */
export function nextCheckText(nextSyncAt: string | null, now: number): string {
  if (!nextSyncAt || new Date(nextSyncAt).getTime() <= now) return "The next check is due within 10 minutes.";
  return `The next check is ${formatRelativeTime(nextSyncAt, { now: new Date(now) })}.`;
}

/** How often a supplier's catalog is checked, learned from how often it changes; the next check in a tooltip. */
export function CheckCadence({
  supplier,
}: {
  supplier: Pick<Supplier, "sync_enabled" | "sync_interval_hours" | "next_sync_at">;
}) {
  const now = useNow(60_000);
  if (!supplier.sync_enabled) return null;
  const hours = supplier.sync_interval_hours;
  return (
    <Tooltip
      content={`${nextCheckText(supplier.next_sync_at, now)} Checks come sooner after one finds new prices, stock or listings, and later after one finds nothing new.`}
    >
      <button type="button" className={styles.tipTrigger}>
        <span className={styles.cadence}>{hours === null ? "first check soon" : checkCadence(hours)}</span>
      </button>
    </Tooltip>
  );
}

/** "Ships to Canada" with an icon: color is never the only signal. Unknown is neutral and dashed. */
export function ShipsToBadge({ value, country }: { value: boolean | null; country: string }) {
  const place = countryName(country);
  if (value === true) {
    return (
      <Badge tone="success" icon="check">
        Ships to {place}
      </Badge>
    );
  }
  if (value === false) {
    return (
      <Badge tone="danger" icon="x">
        Doesn’t ship to {place}
      </Badge>
    );
  }
  return (
    <Badge icon="helpCircle" dashed>
      Not stated
    </Badge>
  );
}

/** Scraping difficulty 1–5 as five segments plus the number (1 = open JSON feed, 5 = walled). */
export function DifficultyMeter({ value }: { value: number | null }) {
  if (value === null) return <span className={styles.missing}>—</span>;
  return (
    <span className={styles.difficulty} data-level={value}>
      <span className={styles.segments} aria-hidden="true">
        {[1, 2, 3, 4, 5].map((step) => (
          <span key={step} className={styles.segment} data-filled={step <= value || undefined} />
        ))}
      </span>
      <span className={styles.difficultyText}>
        {value}
        <VisuallyHidden> of 5</VisuallyHidden>
      </span>
    </span>
  );
}

const STATUS_LOOK: Record<SupplierStatus, { label: string; tone: "neutral" | "warning" | "danger" }> = {
  active: { label: "Active", tone: "neutral" },
  defunct: { label: "Closed", tone: "danger" },
  blocked: { label: "Blocks automated access", tone: "warning" },
};

export function SupplierStatusBadge({ status }: { status: SupplierStatus }) {
  if (status === "active") return null;
  const look = STATUS_LOOK[status];
  return (
    <Badge tone={look.tone} icon="alertTriangle">
      {look.label}
    </Badge>
  );
}

/** The last catalog sync: when, and a badge if it didn't finish cleanly. */
export function LastSync({ run }: { run: SyncRun | null }) {
  if (!run) return <span className={styles.missing}>Never</span>;
  if (run.status === "done") return <RelativeTime value={run.finished_at ?? run.started_at} />;
  const look =
    run.status === "running"
      ? { tone: "info" as const, label: "Running", icon: "refresh" as const }
      : run.status === "partial"
        ? { tone: "warning" as const, label: "Paused", icon: "alertTriangle" as const }
        : { tone: "danger" as const, label: "Failed", icon: "x" as const };
  return (
    <span className={styles.lastSync}>
      <Tooltip content={run.error || look.label}>
        <button type="button" className={styles.tipTrigger}>
          <Badge tone={look.tone} icon={look.icon}>
            {look.label}
          </Badge>
        </button>
      </Tooltip>
      <RelativeTime value={run.started_at} />
    </span>
  );
}
